import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import './SceneDetails.css';
import './SceneInfo.css';
import './ElementsTable.css';
import './ElementTabs.css';
import { useTabCache } from '../../../contexts/TabCacheContext';
import { useAutoFillLocation } from '../../../components/InputRecommend/useAutoFillLocation';
import AlertDialog from '../../../components/UI/AlertDialog/AlertDialog';
import InputRecommendNEW from '../../../components/InputRecommend/InputRecommendNEW';
import DropdownMenu from '../../../components/DropdownMenu/DropdownMenu';
import { MenuPosition } from '../../../components/DropdownMenu/useDropdownMenuField';
import { SceneInteriorExterior, SceneDayNight } from '../../../types/scene.types';
import ElementSearch from '../../../components/ElementSearch/ElementSearch';
import ButtonDialog from '../../../components/ButtonDialog';
import { useSceneFieldInputHandlers } from '../shortcut/useSceneFieldInputHandlers';
import { updateHandlersMap } from '../../../utils/updateHandlersMap';
import { useUnifiedFieldEdit } from '../shortcut/useUnifiedFieldEdit';
import { useElementsData } from './hooks/useElementsData';
import { useDuplicateCheck } from './hooks/useDuplicateCheck';
import { useElementUpdates } from '../../../hooks/dataUpdates/useElementUpdates';
import { sortElements } from '../../../utils/elementSortUtils';
import {
  ELEMENT_TABLE_ROW_HEIGHT_PX,
  buildElementColumnsThreeWay,
  countRowsWithCategoryHeaders,
} from '../../../utils/elementTableLayout';
import { useBatchPasteElements, clearClipboard, setClipboardFromFullScene } from './hooks/useBatchPasteElements';
import { useElementDuplicateCheck } from './hooks/useElementDuplicateCheck';
import { useElementOperations } from './hooks/useElementOperations';
import { useSceneData } from './hooks/useSceneData';
import { useSceneUpdate } from './hooks/useSceneUpdate';
import {
  validatePageNumberInput,
  validateAndFormatPageNumber,
  formatPageNumberDisplay
} from '../../../utils/pageNumberUtils';
import { sortCastItemsByNO } from '../../../utils/elementSortUtils';
import { 
  SceneInfo as SceneInfoType
} from '../../../types/scene.types';
import { 
  ElementWithUserNames,
  ElementType, 
} from '../../../types/element.types';
// 导入 SVG 图标组件
import { ReactComponent as CopyIcon } from '../../../assets/svg/Breakdown/copy.svg';
import { ReactComponent as CloseIcon } from '../../../assets/svg/Breakdown/close.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/svg/Breakdown/delete.svg';
import { ReactComponent as RemoveIcon } from '../../../assets/svg/Breakdown/remove.svg';
import { ReactComponent as WarningIcon } from '../../../assets/svg/Breakdown/warning.svg';
import { ReactComponent as CastItemSvgIcon } from '../../../assets/svg/Breakdown/Elements/cast.svg';
import { ReactComponent as FeaturedRoleIcon } from '../../../assets/svg/Breakdown/Elements/featured.svg';
import { ReactComponent as ExtrasStandInActorIcon } from '../../../assets/svg/Breakdown/Elements/background.svg';
import { ReactComponent as ActionActorIcon } from '../../../assets/svg/Breakdown/Elements/stunt_players.svg';
import { ReactComponent as AnimalIcon } from '../../../assets/svg/Breakdown/Elements/animals.svg';
import { ReactComponent as VehicleIcon } from '../../../assets/svg/Breakdown/Elements/vehicles.svg';
import { ReactComponent as ArtPropIcon } from '../../../assets/svg/Breakdown/Elements/art_props.svg';
import { ReactComponent as CostumeIcon } from '../../../assets/svg/Breakdown/Elements/wardrobe.svg';
import { ReactComponent as MakeupIcon } from '../../../assets/svg/Breakdown/Elements/hair_makeup.svg';
import { ReactComponent as SfxMuIcon } from '../../../assets/svg/Breakdown/Elements/sfx_mu.svg';
import { ReactComponent as CameraIcon } from '../../../assets/svg/Breakdown/Elements/camera.svg';
import { ReactComponent as LightingIcon } from '../../../assets/svg/Breakdown/Elements/lighting.svg';
import { ReactComponent as PracticalEffectsIcon } from '../../../assets/svg/Breakdown/Elements/sfx.svg';
import { ReactComponent as VfxIcon } from '../../../assets/svg/Breakdown/Elements/vfx.svg';
import { ReactComponent as SoundIcon } from '../../../assets/svg/Breakdown/Elements/sound.svg';
import { ReactComponent as ExternalStaffIcon } from '../../../assets/svg/Breakdown/Elements/addl_labor.svg';
import { ReactComponent as SpecialEquipmentIcon } from '../../../assets/svg/Breakdown/Elements/special_equipment.svg';
import { ReactComponent as OtherIcon } from '../../../assets/svg/Breakdown/Elements/misc.svg';
import { ReactComponent as NoteIcon } from '../../../assets/svg/Breakdown/Elements/note.svg';
// 注意：FieldNavigation 和 useFieldState 已移除，统一导航系统由父组件 Breakdown.tsx 管理
// FieldPosition 类型仍在使用（用于 fieldPositionMap 和 elementInputsPositionMap）
type FieldPosition = { row: number; col: number };
// 场次元素类型图标（已注释，未使用）
// import animalIcon from '../../../assets/images/Scene/Item/ANIMAL.png';
// import artPropIcon from '../../../assets/images/Scene/Item/ART_PROP.png';
// import cameraIcon from '../../../assets/images/Scene/Item/CAMERA.png';
// import actionActorIcon from '../../../assets/images/Scene/Item/ACTION_ACTOR.png';
// import costumeIcon from '../../../assets/images/Scene/Item/COSTUME.png';
// import externalStaffIcon from '../../../assets/images/Scene/Item/EXTERNAL_STAFF.png';
// import extrasStandInActorIcon from '../../../assets/images/Scene/Item/EXTRAS_STAND_IN_ACTOR.png';
// import featuredRoleIcon from '../../../assets/images/Scene/Item/FEATURED_ROLE.png';
// import lightingIcon from '../../../assets/images/Scene/Item/LIGHTING.png';
// import makeupIcon from '../../../assets/images/Scene/Item/MAKEUP.png';
// import noteIcon from '../../../assets/images/Scene/Item/NOTE.png';
// import otherIcon from '../../../assets/images/Scene/Item/OTHER.png';
// import practicalEffectsIcon from '../../../assets/images/Scene/Item/PRACTICAL_EFFECTS.png';
// import sfxMakeupIcon from '../../../assets/images/Scene/Item/SFX_MAKEUP.png';
// import soundIcon from '../../../assets/images/Scene/Item/SOUND.png';
// import specialEquipmentIcon from '../../../assets/images/Scene/Item/SPECIAL_EQUIPMENT.png';
// import vehicleIcon from '../../../assets/images/Scene/Item/VEHICLE.png';
// import vfxIcon from '../../../assets/images/Scene/Item/VFX.png';

// 注意：clipboard 已移至 useBatchPasteElements.ts Hook 中

// 注意：formatDecimal 函数已移除，统一使用 formatPageNumberDisplay

interface SceneProps {
  sceneId: string;
  visible: boolean;
  onClose: () => void;
  onSceneUpdate?: (sceneId: string, updatedData: Partial<SceneInfoType>) => void;
  externalSceneData?: Partial<SceneInfoType>; // 外部传入的场次数据更新
  refreshTrigger?: number; // 外部刷新触发器
  onCopyScene?: (sceneId: string) => Promise<void>; // 复制场次回调
  onDeleteScene?: (sceneId: string) => Promise<void>; // 删除场次回调
  onElementRemoved?: () => void; // 场次元素移除回调，用于通知父组件更新已选择元素列表
  sceneOrder?: number; // 当前场次序号
  totalScenes?: number; // 总场次数量（场次行数）
  pagesUpToScene?: number; // 按当前排序的页数累计至当前场次（取整）
  totalPages?: number; // 总累计页数
  onElementsChange?: (items: ElementWithUserNames[]) => void; // 通知父组件场次元素列表变化
  allScenes?: SceneInfoType[]; // 本分场下场次行列表（用于 InputRecommend 等推荐数据源，由父组件传入）
  externalElementSearch?: boolean; // 是否使用外部 ElementSearch
  onOpenElementSearch?: (type: ElementType) => void; // 外部打开 ElementSearch 的回调
  hideSummaryInfo?: boolean; // 是否隐藏场景摘要信息
  hideHeaderActions?: boolean; // 是否隐藏头部操作按钮
  showCloseButton?: boolean; // 是否显示关闭按钮
  /** 隐藏表格内分类标题行旁的「复制该分类」按钮（如 EditScene 抽屉内） */
  hideCategoryHeaderCopy?: boolean;
  canEdit?: boolean;

  // 统一导航系统
  currentStateAndPosition?: import('../shortcut/useUnifiedNavigationState').CurrentStateAndPosition | null;
  getPositionState?: (position: import('../shortcut/useUnifiedNavigationState').UnifiedPosition) => import('../shortcut/useUnifiedNavigationState').UnifiedState;
  sceneDetailsElementRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
  elementTableElementRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
  onElementsChangeForNav?: (items: ElementWithUserNames[]) => void; // 用于统一导航系统获取场次元素列表
}

export interface SceneRef {
  clearActiveTab: () => void;
  getElements: () => ElementWithUserNames[]; // 获取场次元素列表
}

const SceneDetails = forwardRef<SceneRef, SceneProps>(({ 
  sceneId, 
  visible, 
  onClose, 
  onSceneUpdate, 
  externalSceneData, 
  refreshTrigger, 
  onCopyScene, 
  onDeleteScene, 
  onElementRemoved, 
  sceneOrder, 
  totalScenes, 
  pagesUpToScene, 
  totalPages,
  onElementsChange,
  allScenes: externalAllScenes,
  externalElementSearch = false,
  onOpenElementSearch,
  hideSummaryInfo = false,
  hideHeaderActions = false,
  showCloseButton = false,
  hideCategoryHeaderCopy = false,
  canEdit = false,
  // 统一导航系统
  currentStateAndPosition,
  getPositionState,
  sceneDetailsElementRefs,
  elementTableElementRefs,
  onElementsChangeForNav,
}, ref) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { setCache: setTabCache } = useTabCache();
  const readonlyContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const content = readonlyContentRef.current as (HTMLDivElement & { inert: boolean }) | null;
    if (content) content.inert = !canEdit;
  }, [canEdit]);
  // 注意：删除关系操作已移至 ElementsTable 组件内部
  
  // 生成标签页ID（基于 sceneId）
  const tabId = `scene-${sceneId}`;
  
  // 使用 Scene 页面专用的字段输入处理器（自动使用统一 Hook）
  const createFieldHandlers = useSceneFieldInputHandlers();
  
  // 复制按钮加载状态
  const [isCopying, setIsCopying] = useState(false);
  // 删除按钮加载状态
  const [isDeleting, setIsDeleting] = useState(false);
  
  // AlertDialog 状态管理
  const [alertDialogVisible, setAlertDialogVisible] = useState(false);
  const [alertDialogContent, setAlertDialogContent] = useState('');
  
  // 字段ref引用
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  
  // scene-details-container 的 ref（用于限制键盘导航范围）
  const sceneDetailsContainerRef = useRef<HTMLDivElement>(null);
  
  // elements-container 的 ref（用于 elementTable 的导航）
  const elementsContainerRef = useRef<HTMLDivElement>(null);
  
  // 定义字段顺序和位置映射
  // 字段顺序：内外 - 日夜 - 场景 - 剧情日 - 页数 - 剧情 - 拍摄地点 - 日历摘要
  // 根据实际布局：左边容器和右边容器是并排的
  // 左边：第一行（内外、日夜），第二行（剧情日、页数），第三行（拍摄地点）
  // 右边：第一行（场景），第二行（剧情），第三行（日历摘要）
  // Tab 顺序：内外(0,0) - 日夜(0,1) - 场景(1,0) - 剧情日(2,0) - 页数(2,1) - 剧情(3,0) - 拍摄地点(4,0) - 日历摘要(5,0)
  // 注意：由于左右容器并排，我们需要用线性顺序来表示 Tab 导航顺序
  const fieldPositionMap = useMemo(() => {
    const map = new Map<string, FieldPosition>();
    // 使用线性顺序（row 表示在 Tab 顺序中的位置，col 表示在同一行中的位置）
    map.set('sceneInteriorExterior', { row: 0, col: 0 }); // 内外 - Tab顺序第0个
    map.set('sceneDayNight', { row: 0, col: 1 }); // 日夜 - Tab顺序第1个
    map.set('sceneSet', { row: 1, col: 0 }); // 场景 - Tab顺序第2个
    map.set('scenePlotDay', { row: 2, col: 0 }); // 剧情日 - Tab顺序第3个
    map.set('scenePagesNumber', { row: 2, col: 1 }); // 页数 - Tab顺序第4个
    map.set('scenePlot', { row: 3, col: 0 }); // 剧情 - Tab顺序第5个
    map.set('sceneLocation', { row: 4, col: 0 }); // 拍摄地点 - Tab顺序第6个
    map.set('scenePlotSummary', { row: 5, col: 0 }); // 日历摘要 - Tab顺序第7个
    return map;
  }, []);
  
  // 字段顺序数组（与位置映射匹配）
  const fieldOrder = useMemo(() => [
    'sceneInteriorExterior', // 内外 (row=0, col=0)
    'sceneDayNight',         // 日夜 (row=0, col=1)
    'sceneSet',             // 场景 (row=1, col=0)
    'scenePlotDay',         // 剧情日 (row=2, col=0)
    'scenePagesNumber',     // 页数 (row=2, col=1)
    'scenePlot',            // 剧情 (row=3, col=0)
    'sceneLocation',         // 拍摄地点 (row=4, col=0)
    'scenePlotSummary',     // 日历摘要 (row=5, col=0)
  ], []);
  
  // 注意：旧的状态管理（useFieldState）已移除，现在使用统一导航系统（由父组件 Breakdown.tsx 管理）

  // 使用 useEffect 动态更新字段的 data 属性和状态类名
  // 注意：状态管理已由统一导航系统处理，这里只设置 data 属性并注册元素引用
  useEffect(() => {
    fieldOrder.forEach((fieldName: string) => {
      const position = fieldPositionMap.get(fieldName);
      if (!position) return;
      
      const fieldElement = fieldRefs.current[fieldName];
      if (!fieldElement) return;
      
      // 设置 data 属性
      fieldElement.setAttribute('data-field', fieldName);
      fieldElement.setAttribute('data-row', String(position.row));
      fieldElement.setAttribute('data-col', String(position.col));
      
      // 注册到统一导航系统的元素引用映射
      if (sceneDetailsElementRefs) {
        sceneDetailsElementRefs.current.set(fieldName, fieldElement);
      }
      
      // 使用统一导航系统获取状态并更新 CSS 类
      // 注意：getPositionState 通过闭包访问最新的 currentStateAndPosition，不需要放在依赖项中
      if (getPositionState) {
        const unifiedPosition: import('../shortcut/useUnifiedNavigationState').UnifiedPosition = {
          type: 'sceneDetails',
          sceneID: sceneId,
          field: fieldName,
        };
        const state = getPositionState(unifiedPosition);
        
        // 移除状态类
        fieldElement.classList.remove('selected', 'editing', 'hover');
        
        // 添加状态类
        if (state === 'selected') {
          fieldElement.classList.add('selected');
        } else if (state === 'editing') {
          fieldElement.classList.add('editing');
        } else if (state === 'hover') {
          // hover 状态通过 CSS :hover 处理，不需要添加类
        }
      }
    });
    // 注意：getPositionState 是 useCallback，它的引用会随 currentStateAndPosition 变化而变化
    // 但我们在 useEffect 内部调用它时，它会通过闭包访问最新的 currentStateAndPosition
    // 所以只需要依赖 currentStateAndPosition，不需要依赖 getPositionState
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldOrder, fieldPositionMap, sceneId, sceneDetailsElementRefs, currentStateAndPosition]);

  
  // 场次元素类型图标映射（未使用）
  // const elementTypeIcons: Record<ElementType, string> = {
  //   [ElementType.FEATURED]: featuredRoleIcon,
  //   [ElementType.STUNT_PLAYERS]: actionActorIcon,
  //   [ElementType.BACKGROUND]: extrasStandInActorIcon,
  //   [ElementType.ADDL_LABOR]: externalStaffIcon,
  //   [ElementType.ANIMALS]: animalIcon,
  //   [ElementType.VEHICLES]: vehicleIcon,
  //   [ElementType.ART_PROPS]: artPropIcon,
  //   [ElementType.WARDROBE]: costumeIcon,
  //   [ElementType.HAIR_MAKEUP]: makeupIcon,
  //   [ElementType.SFX_MU]: sfxMakeupIcon,
  //   [ElementType.CAMERA]: cameraIcon,
  //   [ElementType.LIGHTING]: lightingIcon,
  //   [ElementType.SOUND]: soundIcon,
  //   [ElementType.VFX]: vfxIcon,
  //   [ElementType.SFX]: practicalEffectsIcon,
  //   [ElementType.SPECIAL_EQUIPMENT]: specialEquipmentIcon,
  //   [ElementType.NOTE]: noteIcon,
  //   [ElementType.MISC]: otherIcon
  // };
  const [sceneInfo, setSceneInfo] = useState<SceneInfoType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ElementType | null>(null);
  // 存储整个分场的所有场次数据（用于推荐数据源）
  const [allScenes, setAllScenes] = useState<SceneInfoType[]>([]);
  
  // 本地输入框状态（用于文本输入）
  const [localPlotDay, setLocalPlotDay] = useState('');
  // 页数字段的本地输入值（参考场次表的实现，使用 undefined 表示未编辑状态）
  const [pagesNumberInputValue, setPagesNumberInputValue] = useState<string | undefined>(undefined);
  const [sceneLocationDisplayText, setSceneLocationDisplayText] = useState('');
  // 场号内联编辑状态
  const [isEditingSceneCode, setIsEditingSceneCode] = useState(false);
  const [localSceneCode, setLocalSceneCode] = useState('');
  
  // 场号输入框的回车键处理（独立输入框，不使用统一状态管理）
  const handleSceneCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // 阻止事件传播到全局处理器
      e.currentTarget.blur(); // 触发 blur 事件，从而调用 handleSceneCodeBlur
    }
  };
  
  // 自动填充地点的 Hook
  const { checkAndAutoFillLocation } = useAutoFillLocation({
    sceneInfo,
    onUpdateLocation: (location) => handleSceneUpdate('sceneLocation', location),
    onUpdateDisplayText: setSceneLocationDisplayText,
  });
  
  // 注意：handleSetDisplayTextCache 已移除，不再需要
  
  
  // 操作菜单状态
  const [activeOperationsTab, setActiveOperationsTab] = useState<ElementType | null>(null);
  const [operationsMenuVisible, setOperationsMenuVisible] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [operationsMenuPosition, setOperationsMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeOperationsButton, setActiveOperationsButton] = useState<ElementType | null>(null);
  const operationsButtonRefs = useRef<Record<ElementType, HTMLDivElement | null>>({} as Record<ElementType, HTMLDivElement | null>);

  // 下拉菜单状态管理（详情页模式）
  const [interiorExteriorMenuVisible, setInteriorExteriorMenuVisible] = useState(false);
  const [interiorExteriorMenuPosition, setInteriorExteriorMenuPosition] = useState<MenuPosition | null>(null);
  const [dayNightMenuVisible, setDayNightMenuVisible] = useState(false);
  const [dayNightMenuPosition, setDayNightMenuPosition] = useState<MenuPosition | null>(null);

  // 计算菜单位置（详情页）：视口坐标 + 配合 DropdownMenu useFixedPosition，避免 EditScene 等父级 overflow 裁切
  const calculateDetailMenuPosition = useCallback((buttonElement: HTMLElement | null): MenuPosition | null => {
    if (!buttonElement) {
      return null;
    }
    if (!buttonElement.closest('.scene-detail-input-container')) {
      return null;
    }
    const rect = buttonElement.getBoundingClientRect();
    return {
      top: rect.bottom + 2,
      left: rect.left,
      width: rect.width
    };
  }, []);

  // 内外按钮 ref
  const interiorExteriorButtonRef = useRef<HTMLButtonElement | null>(null);
  // 日夜按钮 ref
  const dayNightButtonRef = useRef<HTMLButtonElement | null>(null);

  // 菜单打开时在同一帧提交后、绘制前用 ref 计算 fixed 位置（避免 rAF + e.currentTarget 失效、且保证能渲染出 DropdownMenu）
  useLayoutEffect(() => {
    if (!interiorExteriorMenuVisible) {
      return;
    }
    const button = interiorExteriorButtonRef.current;
    if (!button) return;
    const position = calculateDetailMenuPosition(button);
    if (position) {
      setInteriorExteriorMenuPosition(position);
    }
  }, [interiorExteriorMenuVisible, calculateDetailMenuPosition]);

  useLayoutEffect(() => {
    if (!dayNightMenuVisible) {
      return;
    }
    const button = dayNightButtonRef.current;
    if (!button) return;
    const position = calculateDetailMenuPosition(button);
    if (position) {
      setDayNightMenuPosition(position);
    }
  }, [dayNightMenuVisible, calculateDetailMenuPosition]);

  // 监听内外按钮的 focus/blur 事件（sceneInfo 就绪后按钮才挂载，需随 sceneInfo 重新绑定）
  useEffect(() => {
    const button = interiorExteriorButtonRef.current;
    if (!button) return;

    const handleFocus = () => {
      setInteriorExteriorMenuVisible(true);
      setDayNightMenuVisible(false);
    };

    const handleBlur = (e: FocusEvent) => {
      // 延迟关闭，给选择菜单项的时间
      setTimeout(() => {
        if (!button.contains(e.relatedTarget as Node)) {
          setInteriorExteriorMenuVisible(false);
        }
      }, 150);
    };

    button.addEventListener('focus', handleFocus);
    button.addEventListener('blur', handleBlur);

    return () => {
      button.removeEventListener('focus', handleFocus);
      button.removeEventListener('blur', handleBlur);
    };
  }, [calculateDetailMenuPosition, sceneInfo?.sceneID]);

  // 监听日夜按钮的 focus/blur 事件
  useEffect(() => {
    const button = dayNightButtonRef.current;
    if (!button) return;

    const handleFocus = () => {
      setDayNightMenuVisible(true);
      setInteriorExteriorMenuVisible(false);
    };

    const handleBlur = (e: FocusEvent) => {
      // 延迟关闭，给选择菜单项的时间
      setTimeout(() => {
        if (!button.contains(e.relatedTarget as Node)) {
          setDayNightMenuVisible(false);
        }
      }, 150);
    };

    button.addEventListener('focus', handleFocus);
    button.addEventListener('blur', handleBlur);

    return () => {
      button.removeEventListener('focus', handleFocus);
      button.removeEventListener('blur', handleBlur);
    };
  }, [calculateDetailMenuPosition, sceneInfo?.sceneID]);

  // 监听编辑状态变化，当内外、日夜字段进入编辑状态时打开菜单
  useEffect(() => {
    // EditScene 等未传入统一导航状态时，不要用本 effect 反复把下拉设为关闭（否则会与点击打开互相打架）
    if (currentStateAndPosition === undefined) {
      return;
    }
    if (currentStateAndPosition === null || currentStateAndPosition.type !== 'sceneDetails') {
      // 如果不在 sceneDetails 编辑状态，关闭所有菜单
      setInteriorExteriorMenuVisible(false);
      setDayNightMenuVisible(false);
      return;
    }

    const { field, state } = currentStateAndPosition;
    const isEditing = state === 'editing';

    // 如果退出编辑状态，关闭所有菜单
    if (!isEditing) {
      setInteriorExteriorMenuVisible(false);
      setDayNightMenuVisible(false);
      return;
    }

    // 如果进入编辑状态，根据 field 打开对应的菜单（位置由 useLayoutEffect 同步）
    if (field === 'sceneInteriorExterior') {
      setInteriorExteriorMenuVisible(true);
      // 关闭另一个菜单
      setDayNightMenuVisible(false);
    } else if (field === 'sceneDayNight') {
      setDayNightMenuVisible(true);
      // 关闭另一个菜单
      setInteriorExteriorMenuVisible(false);
    } else {
      // 如果编辑的是其他字段，关闭所有下拉菜单
      setInteriorExteriorMenuVisible(false);
      setDayNightMenuVisible(false);
    }
  }, [currentStateAndPosition, calculateDetailMenuPosition]);
  
  // ====== 场次元素区域高度：只信 flex 分给 .elements-section 的真实高度（ResizeObserver），不再做减法估算 ======
  const elementsSectionRef = useRef<HTMLDivElement | null>(null);

  const [elementsHeight, setElementsHeight] = useState<number>(ELEMENT_TABLE_ROW_HEIGHT_PX);

  // 同步 sceneInfo 的值到本地输入框状态
  // 注意：页数字段完全由本地状态管理（参考场次表的实现），只在初始化时同步一次
  // 使用 useRef 跟踪上一次的关键字段值，避免不必要的更新
  const prevValuesRef = useRef<{ plotDay?: string; pagesNumber?: number; sceneCode?: string } | null>(null);
  const isInitialMountRef = useRef(true);
  
  useEffect(() => {
    if (sceneInfo) {
      // 检查关键字段是否有变化，避免不必要的更新
      const plotDay = sceneInfo.scenePlotDay !== null && sceneInfo.scenePlotDay !== undefined ? String(sceneInfo.scenePlotDay) : '';
      const pagesNumber = sceneInfo.scenePagesNumber;
      const sceneCode = sceneInfo.sceneCode || '';
      
      const prev = prevValuesRef.current;
      const hasChanged = !prev || 
          prev.plotDay !== plotDay ||
          prev.pagesNumber !== pagesNumber ||
          prev.sceneCode !== sceneCode;
      
      // 只在值真正变化时才更新本地状态
      if (hasChanged) {
        setLocalPlotDay(plotDay);
        // 页数字段完全由本地状态管理（pagesNumberInputValue），不在这里同步（参考场次表的实现）
        // 场号字段不受编辑状态影响，始终同步
        setLocalSceneCode(sceneCode);
        prevValuesRef.current = { plotDay, pagesNumber, sceneCode };
      }
      
      // 标记初始化完成
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }
    } else {
      // sceneInfo 为 null 时清空本地状态
      if (prevValuesRef.current) {
        setLocalPlotDay('');
        // 页数字段完全由本地状态管理，不在这里清空
        setLocalSceneCode('');
        prevValuesRef.current = null;
      }
    }
  }, [sceneInfo]);

  // 当 sceneId 变化时，重置页数字段的本地输入值（参考场次表的实现）
  useEffect(() => {
    setPagesNumberInputValue(undefined);
  }, [sceneId, setPagesNumberInputValue]);

  const canMeasureElementsSection =
    visible && Boolean(sceneInfo) && !loading && !error;

  useEffect(() => {
    if (!canMeasureElementsSection) return;
    const el = elementsSectionRef.current;
    if (!el) return;

    const applyHeight = (raw: number) => {
      const h = Math.floor(raw);
      if (h <= 0) return;
      setElementsHeight((prev) =>
        prev === h ? prev : Math.max(ELEMENT_TABLE_ROW_HEIGHT_PX, h)
      );
    };

    applyHeight(el.getBoundingClientRect().height);

    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      applyHeight(cr.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [canMeasureElementsSection, sceneId]);
  
  // 注意：onElementsChange 的同步移至 effectiveElements 定义之后，使用 effectiveElements 确保传递正确的数据源

  // 数据获取 Hook（需要在状态声明之后）
  useSceneData(
    sceneId,
    visible,
    setSceneInfo,
    setLoading,
    setError,
    setSceneLocationDisplayText,
    setAllScenes,
    setActiveTab,
    setLocalPlotDay,
    setPagesNumberInputValue,
    setLocalSceneCode,
    setIsEditingSceneCode,
    setActiveOperationsTab,
    setOperationsMenuVisible,
    sceneInfo,
    externalAllScenes,
    externalSceneData,
    refreshTrigger
  );
  
  // 场次更新 Hook（需要在 sceneInfo 声明之后）
  const { handleSceneUpdate, flushSceneUpdate } = useSceneUpdate(
    sceneInfo,
    setSceneInfo,
    sceneId,
    onSceneUpdate
  );

  // 重名检查 Hook - 需要在 useDuplicateCheck 之前定义
  const { checkDuplicateName } = useElementDuplicateCheck(sceneInfo);

  // SceneInfo 组件内的本地输入值存储（用于其他字段，避免输入过程中触发提交和同步）
  const [localInputValues, setLocalInputValues] = React.useState<Map<string, string>>(new Map());

  // 标签列表（按需求顺序）- 需要在 useEffect 之前定义，使用 useMemo 避免每次渲染都创建新数组
  const tabs: ElementType[] = React.useMemo(() => [
    ElementType.CAST,
    ElementType.FEATURED,
    ElementType.BACKGROUND,
    ElementType.STUNT_PLAYERS,
    ElementType.ANIMALS,
    ElementType.VEHICLES,
    ElementType.ART_PROPS,
    ElementType.WARDROBE,
    ElementType.HAIR_MAKEUP,
    ElementType.SFX_MU,
    ElementType.CAMERA,
    ElementType.LIGHTING,
    ElementType.SFX,
    ElementType.VFX,
    ElementType.SOUND,
    ElementType.ADDL_LABOR,
    ElementType.SPECIAL_EQUIPMENT,
    ElementType.MISC,
    ElementType.NOTE
  ], []);

  // ElementTabs 状态管理（仅在非外部模式下使用）
  const [elementSearchVisible, setElementSearchVisible] = useState(false);
  const [elementSearchType, setElementSearchType] = useState<ElementType>(ElementType.CAST);

  // ElementsTable 状态和逻辑
  const { elements: elementsFromData, breakdownId: breakdownIdFromData } = useElementsData(sceneId);
  const { updateElementField, deleteElementRelationData, createElementRelationData } = useElementUpdates();

  // 列表唯一来源：useElementsData 订阅 Item + Relation 缓存；useSceneData 拉数后写入全局缓存
  const effectiveElements = elementsFromData;
  const effectiveBreakdownId = breakdownIdFromData || sceneInfo?.sceneBelongBreakdownID || '';

  const { handleBatchPaste, clipboardCount } = useBatchPasteElements(
    effectiveElements,
    sceneId,
    sceneInfo?.sceneBelongBreakdownID,
    onElementRemoved
  );

  // 暴露给父组件的方法（与界面一致使用 effectiveElements）
  useImperativeHandle(
    ref,
    () => ({
      clearActiveTab: () => {
        setActiveTab(null);
      },
      getElements: () => effectiveElements,
    }),
    [effectiveElements]
  );

  // 通知父组件场次元素列表变化（使用 effectiveElements 确保传递正确的数据源，含缓存数据）
  useEffect(() => {
    if (onElementsChange) {
      onElementsChange(effectiveElements);
    }
  }, [effectiveElements, onElementsChange]);

  // ElementsTable 内部状态管理
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{[key: string]: string}>({});
  const editValuesRef = useRef<{[key: string]: string}>({});
  const elementsFieldRefs = useRef<Map<string, HTMLElement | null>>(new Map());
  const [duplicateNameMap, setDuplicateNameMap] = useState<Map<string, boolean>>(new Map());
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState<ElementType | null>(null);
  const deleteButtonRefs = useRef<Record<ElementType, React.RefObject<HTMLButtonElement>>>({} as Record<ElementType, React.RefObject<HTMLButtonElement>>);
  // SVG 图标组件映射（用于 ElementsTable 和 ElementTabs）
  const elementTypeSvgIcons: Record<ElementType, React.FC<React.SVGProps<SVGSVGElement>>> = {
    [ElementType.CAST]: CastItemSvgIcon,
    [ElementType.FEATURED]: FeaturedRoleIcon,
    [ElementType.BACKGROUND]: ExtrasStandInActorIcon,
    [ElementType.STUNT_PLAYERS]: ActionActorIcon,
    [ElementType.ANIMALS]: AnimalIcon,
    [ElementType.VEHICLES]: VehicleIcon,
    [ElementType.ART_PROPS]: ArtPropIcon,
    [ElementType.WARDROBE]: CostumeIcon,
    [ElementType.HAIR_MAKEUP]: MakeupIcon,
    [ElementType.SFX_MU]: SfxMuIcon,
    [ElementType.CAMERA]: CameraIcon,
    [ElementType.LIGHTING]: LightingIcon,
    [ElementType.SFX]: PracticalEffectsIcon,
    [ElementType.VFX]: VfxIcon,
    [ElementType.SOUND]: SoundIcon,
    [ElementType.ADDL_LABOR]: ExternalStaffIcon,
    [ElementType.SPECIAL_EQUIPMENT]: SpecialEquipmentIcon,
    [ElementType.MISC]: OtherIcon,
    [ElementType.NOTE]: NoteIcon,
  };

  // 初始化每个分类的 ref
  useEffect(() => {
    const categories: ElementType[] = tabs;
    categories.forEach(category => {
      if (!deleteButtonRefs.current[category]) {
        deleteButtonRefs.current[category] = React.createRef<HTMLButtonElement>();
      }
    });
  }, [tabs]);

  // 使用统一的字段编辑 Hook
  const { activateFieldEdit } = useUnifiedFieldEdit();

  // 重名检查 Hook
  useDuplicateCheck({
    editingItem,
    editingField,
    editValuesRef,
    elements: effectiveElements,
    onCheckDuplicate: checkDuplicateName,
  });

  // 检查所有元素名称是否有重复
  useEffect(() => {
    if (!checkDuplicateName || effectiveElements.length === 0) {
      setDuplicateNameMap(new Map());
      return;
    }

    const nameCount = new Map<string, number>();
    for (const item of effectiveElements) {
      const name = item.elementName?.trim();
      if (name) nameCount.set(name, (nameCount.get(name) ?? 0) + 1);
    }

    const duplicateMap = new Map<string, boolean>();
    for (const item of effectiveElements) {
      const name = item.elementName?.trim();
      duplicateMap.set(item.elementID, !!name && (nameCount.get(name) ?? 0) > 1);
    }

    setDuplicateNameMap(duplicateMap);
  }, [effectiveElements, checkDuplicateName]);

  // ElementsTable 处理更新操作
  const handleUpdateElement = useCallback(async (
    elementId: string,
    field: string,
    value: any
  ) => {
    if (!effectiveBreakdownId) {
      console.warn('[SceneDetails] breakdownId 不存在，无法更新');
      return;
    }
    
    try {
      await updateElementField(effectiveBreakdownId, elementId, sceneId, field, value);
    } catch (error) {
      console.error('[SceneDetails] 更新失败:', error);
      throw error;
    }
  }, [effectiveBreakdownId, sceneId, updateElementField]);

  // ElementsTable 处理删除操作
  const handleDeleteElement = useCallback(async (elementId: string) => {
    if (!effectiveBreakdownId) {
      console.warn('[SceneDetails] breakdownId 不存在，无法删除');
      return;
    }
    
    try {
      await deleteElementRelationData(effectiveBreakdownId, elementId, sceneId);
      onElementRemoved?.();
    } catch (error) {
      console.error('删除场次元素失败:', error);
      throw error;
    }
  }, [effectiveBreakdownId, sceneId, deleteElementRelationData, onElementRemoved]);

  const sortedEffectiveElements = useMemo(
    () => sortElements(effectiveElements),
    [effectiveElements]
  );

  const elementTableColumns = useMemo(
    () => buildElementColumnsThreeWay(sortedEffectiveElements),
    [sortedEffectiveElements]
  );

  /**
   * 在当前列表高度 h 下，单列可容纳的最大行数 N = floor(h / 行高)。
   * 三列共可容纳 3*N 个「槽位」（分类标题与元素各占一行）。
   * 槽位数 ≤ 3*N 时不出现内部滚动；超出则固定容器高度为 h，列表区域 overflow 滚动。
   */
  const elementLayoutStats = useMemo(() => {
    const totalSlots = countRowsWithCategoryHeaders(sortedEffectiveElements);
    const maxRowsInViewport = Math.max(
      1,
      Math.floor(elementsHeight / ELEMENT_TABLE_ROW_HEIGHT_PX)
    );
    const capacityWithoutScroll = 3 * maxRowsInViewport;
    const needsInnerScroll = totalSlots > capacityWithoutScroll;
    return { totalSlots, maxRowsInViewport, capacityWithoutScroll, needsInnerScroll };
  }, [sortedEffectiveElements, elementsHeight]);

  // elementTable 的字段状态管理（独立的实例）
  // 注意：旧的状态管理（useFieldState for ElementTable）已移除，现在使用统一导航系统（由父组件 Breakdown.tsx 管理）
  
  // 通知父组件场次元素列表变化（用于统一导航系统构建导航地图）
  useEffect(() => {
    if (onElementsChangeForNav) {
      onElementsChangeForNav(effectiveElements);
    }
  }, [effectiveElements, onElementsChangeForNav]);

  // 创建 elementTable 输入框位置映射
  const elementInputsPositionMap = useMemo(() => {
    const map = new Map<string, FieldPosition>();
    
    const columns = elementTableColumns;
    let globalRow = 0;
    
    columns.forEach((column, columnIndex) => {
      column.forEach((item, itemIndex) => {
        if ('isCategoryHeader' in item && item.isCategoryHeader) {
          // 跳过分类头部
          return;
        }
        
        const element = item as ElementWithUserNames;
        const isExtras = element.elementType === ElementType.BACKGROUND;
        const isFeaturedRole = element.elementType === ElementType.FEATURED;
        const isActionActor = element.elementType === ElementType.STUNT_PLAYERS;
        const isAnimal = element.elementType === ElementType.ANIMALS;
        const isVehicle = element.elementType === ElementType.VEHICLES;
        const showQuantity = isExtras || isFeaturedRole || isActionActor || isAnimal || isVehicle;
        
        // 名称输入框
        map.set(`${element.elementID}-name`, { row: globalRow, col: 0 });
        
        // 数量输入框（如果有）
        if (showQuantity) {
          map.set(`${element.elementID}-quantity`, { row: globalRow, col: 1 });
        }
        
        globalRow++;
      });
    });
    
    return map;
  }, [elementTableColumns]);

  // 计算 elementTable 的总行数和列数（用于注释掉的代码）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const elementsTotalRows = useMemo(() => {
    return effectiveElements.length;
  }, [effectiveElements.length]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const elementsTotalCols = 2; // name 和 quantity（如果有）

  // elementTable 的鼠标交互（用于注释掉的代码）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getElementInputPosition = useCallback((element: HTMLElement): FieldPosition | null => {
    const itemId = element.getAttribute('data-item-id');
    const field = element.getAttribute('data-field');
    
    if (!itemId || !field) return null;
    
    const key = `${itemId}-${field}`;
    return elementInputsPositionMap.get(key) || null;
  }, [elementInputsPositionMap]);


  // 使用 useEffect 动态更新 elementTable 输入框的 data 属性和状态类名
  // 注意：状态管理已由统一导航系统处理，这里只设置 data 属性并注册元素引用
  useEffect(() => {
    elementInputsPositionMap.forEach((position: FieldPosition, inputKey: string) => {
      const inputElement = elementsFieldRefs.current.get(inputKey);
      if (!inputElement) return;
      
      // 设置 data 属性（如果还没有）
      const [elementID, field] = inputKey.split('-');
      if (elementID && field) {
        if (!inputElement.getAttribute('data-item-id')) {
          inputElement.setAttribute('data-item-id', elementID);
        }
        if (!inputElement.getAttribute('data-field')) {
          inputElement.setAttribute('data-field', field);
        }
      }
      
      // 注册到统一导航系统的元素引用映射
      if (elementTableElementRefs) {
        elementTableElementRefs.current.set(inputKey, inputElement);
      }
      
      // 使用统一导航系统获取状态并更新 CSS 类
      // 注意：getPositionState 通过闭包访问最新的 currentStateAndPosition，不需要放在依赖项中
      if (getPositionState && elementID && (field === 'name' || field === 'quantity')) {
        const unifiedPosition: import('../shortcut/useUnifiedNavigationState').UnifiedPosition = {
          type: 'elementTable',
          elementID,
          field: field as 'name' | 'quantity',
        };
        const state = getPositionState(unifiedPosition);
        
        // 移除状态类
        inputElement.classList.remove('selected', 'editing', 'hover');
        
        // 添加状态类
        if (state === 'selected') {
          inputElement.classList.add('selected');
        } else if (state === 'editing') {
          inputElement.classList.add('editing');
        } else if (state === 'hover') {
          // hover 状态通过 CSS :hover 处理，不需要添加类
        }
      }
    });
    // 注意：getPositionState 是 useCallback，它的引用会随 currentStateAndPosition 变化而变化
    // 但我们在 useEffect 内部调用它时，它会通过闭包访问最新的 currentStateAndPosition
    // 所以只需要依赖 currentStateAndPosition，不需要依赖 getPositionState
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementInputsPositionMap, elementTableElementRefs, currentStateAndPosition]);

  // ElementsTable 处理输入框激活编辑
  const handleElementInputActivate = (element: HTMLElement, itemId: string, field: string) => {
    const currentItem = effectiveElements.find(item => item.elementID === itemId);
    if (!currentItem) return;

    let originalValue: any;
    if (field === 'name') {
      originalValue = currentItem.elementName || '';
    } else if (field === 'description') {
      originalValue = currentItem.elementAbbr || '';
    } else if (field === 'quantity') {
      originalValue = currentItem.elementQtyInScene?.toString() || '1';
    }

    setEditingItem(itemId);
    setEditingField(field);
    const newEditValues = {
      [`${itemId}-name`]: currentItem.elementName || '',
      [`${itemId}-description`]: currentItem.elementAbbr || '',
      [`${itemId}-quantity`]: currentItem.elementQtyInScene?.toString() || '1'
    };
    setEditValues(newEditValues);
    editValuesRef.current = newEditValues;

    // 检查是否有双击位置（通过临时属性）
    const doubleClickX = (element as any).__doubleClickX;
    const hasDoubleClickX = doubleClickX !== undefined;
    if (hasDoubleClickX) {
      delete (element as any).__doubleClickX;
    }

    activateFieldEdit({
      element,
      originalValue,
      autoFocus: true,
      selectText: false,
      clickX: doubleClickX, // 传递 clickX 用于光标定位
    });
  };

  // ElementsTable 处理输入框值变化
  const handleElementInputChange = (field: string, value: string, itemId: string) => {
    setEditValues(prev => {
      const newValues = {
        ...prev,
        [`${itemId}-${field}`]: value
      };
      editValuesRef.current = newValues;
      
      if ((field === 'name' || field === 'description') && editingItem === itemId && editingField === field) {
        setTimeout(() => {
          if ((editValuesRef as any).__triggerDuplicateCheck) {
            (editValuesRef as any).__triggerDuplicateCheck();
          }
        }, 0);
      }
      
      return newValues;
    });
  };

  // ElementsTable 创建字段失焦处理函数
  const createElementFieldBlurHandler = useCallback(async (e: React.FocusEvent<HTMLInputElement>) => {
    const element = e.target as HTMLElement;
    const itemId = element.getAttribute('data-item-id');
    const field = element.getAttribute('data-field');
    
    if (!itemId || !field) return;
    
    const newValue = editValues[`${itemId}-${field}`];
    const hasDuplicate = element.getAttribute('data-has-duplicate') === 'true';
    
    if ((field === 'name' || field === 'description') && hasDuplicate) {
      e.preventDefault();
      e.stopPropagation();
      setTimeout(() => {
        const input = element as HTMLInputElement;
        input.focus();
      }, 0);
      return;
    }
    
    if (newValue !== undefined) {
      const finalValue = field === 'quantity' 
        ? (newValue === '' || newValue === null || newValue === undefined ? 1 : parseInt(newValue) || 1)
        : newValue;
      
      const fieldName = field === 'name' ? 'elementName' : 
                      field === 'description' ? 'elementAbbr' : 
                      'elementQtyInScene';
      
      try {
        await handleUpdateElement(itemId, fieldName, finalValue);
        setEditingItem(null);
        setEditingField(null);
        setEditValues({});
        editValuesRef.current = {};
      } catch (error) {
        const currentItem = effectiveElements.find(item => item.elementID === itemId);
        if (currentItem) {
          const originalValue = field === 'name' 
            ? currentItem.elementName || ''
            : field === 'description'
              ? currentItem.elementAbbr || ''
              : currentItem.elementQtyInScene?.toString() || '1';
          
          setEditValues(prev => ({
            ...prev,
            [`${itemId}-${field}`]: originalValue
          }));
          editValuesRef.current = {
            ...editValuesRef.current,
            [`${itemId}-${field}`]: originalValue
          };
        }
        console.error('更新失败，已恢复原值:', error);
      }
    } else {
      setEditingItem(null);
      setEditingField(null);
      setEditValues({});
      editValuesRef.current = {};
    }
  }, [editValues, effectiveElements, handleUpdateElement]);

  // ElementTabs 处理打开 ElementSearch
  const handleOpenElementSearchInternal = useCallback((type: ElementType) => {
    if (externalElementSearch && onOpenElementSearch) {
      onOpenElementSearch(type);
    } else {
      setElementSearchType(type);
      setElementSearchVisible(true);
    }
  }, [externalElementSearch, onOpenElementSearch]);

  // ElementTabs 处理关闭 ElementSearch
  const handleCloseElementSearchInternal = useCallback(() => {
    setElementSearchVisible(false);
    setActiveTab(null);
  }, [setActiveTab]);

  // ElementTabs 处理 ElementSearch 选择元素
  const handleElementSearchChangeInternal = useCallback(async (element: any, isCreated?: boolean) => {
    if (!sceneId || !effectiveBreakdownId) return;

    if (isCreated) {
      return;
    }

    try {
      if (effectiveBreakdownId) {
        await createElementRelationData(
          effectiveBreakdownId,
          element.elementID,
          sceneId,
          {
            elementQtyInScene: element.elementMaxQtyInBreakdown || 1,
          }
        );
      }
    } catch (error: any) {
      console.error('创建场次元素关系失败:', error);
      alert(t('scene.createElementRelationFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  }, [sceneId, effectiveBreakdownId, createElementRelationData, t]);

  const selectedElementIds = useMemo(
    () => effectiveElements.map(item => item.elementID),
    [effectiveElements]
  );

  // 统一的字段失焦处理（提交到服务器）
  // 注意：必须在 handleSceneUpdate 定义之后
  const handlePlotDayBlur = useCallback(async (e: React.FocusEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement;
    const val = target.value.trim();
    if (val === '') {
      setLocalPlotDay('');
      handleSceneUpdate('scenePlotDay', undefined);
    } else {
      setLocalPlotDay(val);
      handleSceneUpdate('scenePlotDay', val);
    }
    await flushSceneUpdate();
  }, [handleSceneUpdate, flushSceneUpdate]);
  
  const handlePagesNumberBlur = useCallback(async (e: React.FocusEvent<HTMLElement>) => {
    // 完成输入时，验证、格式化和更新（参考场次表的实现）
    const target = e.target as HTMLInputElement;
    const inputVal = target.value.trim();
    
    // 清除本地输入值
    setPagesNumberInputValue(undefined);
    
    // 如果输入为空，设置为 undefined（清空字段）
    if (inputVal === '') {
      handleSceneUpdate('scenePagesNumber', undefined);
    } else {
      // 验证并格式化输入
      const validatedValue = validateAndFormatPageNumber(inputVal);
      if (validatedValue === undefined) {
        // 如果输入无效，恢复为原始值
        handleSceneUpdate('scenePagesNumber', sceneInfo?.scenePagesNumber);
      } else {
        // 如果输入有效，格式化并更新
        handleSceneUpdate('scenePagesNumber', validatedValue);
      }
    }
    await flushSceneUpdate();
  }, [handleSceneUpdate, sceneInfo, flushSceneUpdate]);
  
  const handlePlotBlur = useCallback(async (e: React.FocusEvent<HTMLElement>) => {
    // 当剧情字段完成输入时，如果日历摘要为空，则将剧情内容同步到日历摘要
    const target = e.target as HTMLInputElement;
    const plotValue = target.value.trim();
    if (plotValue && sceneInfo && (!sceneInfo.scenePlotSummary || sceneInfo.scenePlotSummary.trim() === '')) {
      handleSceneUpdate('scenePlotSummary', plotValue);
    }
    await flushSceneUpdate();
  }, [handleSceneUpdate, flushSceneUpdate, sceneInfo]);
  
  const handlePlotSummaryBlur = useCallback(() => {
    // 空实现，保留接口兼容性
  }, []);

  // 实时同步到场次表的函数（不更新 sceneInfo，不提交到服务器）
  const syncToTable = useCallback((field: string, value: any) => {
    if (onSceneUpdate) {
      onSceneUpdate(sceneId, { [field]: value } as any);
    }
  }, [sceneId, onSceneUpdate]);


  
  // 场号输入框的失焦处理（独立输入框，不使用统一状态管理）
  // 注意：必须在 handleSceneUpdate 定义之后
  const handleSceneCodeBlur = useCallback(async (e: React.FocusEvent<HTMLInputElement>) => {
    // 先退出编辑状态，确保 UI 立即更新
    setIsEditingSceneCode(false);
    // 然后保存值并更新缓存
    const value = localSceneCode.trim();
    handleSceneUpdate('sceneCode', value);
    await flushSceneUpdate();
  }, [localSceneCode, handleSceneUpdate, flushSceneUpdate]);
  
  // 注意：场次元素更新逻辑已移至 ElementsTable 组件内部
  // 不再需要在这里使用 useElementUpdates
  // 注意：checkDuplicateName 已在前面定义（第411行）
  
  // 操作菜单 Hook
  const {
    handleCopyCategoryItems,
    handleClearCategoryItems,
  } = useElementOperations(
    effectiveElements,
    sceneId,
    effectiveBreakdownId,
    onElementRemoved,
    setOperationsMenuVisible,
    setActiveOperationsButton,
    setActiveOperationsTab,
    setOperationsMenuPosition,
    operationsButtonRefs
  );

  // fetchData、refreshElements、restoreFromCache 和相关的 useEffect 已移至 useSceneData Hook

  // 保存状态到缓存（当关键状态变化时）
  useEffect(() => {
    if (!sceneId || !visible || !sceneInfo?.sceneBelongBreakdownID) return;
    
    // 只在有数据时才保存到缓存
    // 注意：使用 sceneId 作为 businessId，而不是 sceneBelongBreakdownID
    // 这样可以避免与分场级别的缓存（breakdownList-xxx）冲突
    if (sceneInfo) {
      setTabCache(tabId, sceneId, location.pathname, {
        sceneLocationDisplayText,
        allScenes,
        activeTab: activeTab || null,
        localPlotDay,
        pagesNumberInputValue: pagesNumberInputValue,
        localSceneCode,
        // 注意：isEditingSceneCode 不保存到缓存，因为场号输入框是独立的，编辑状态不应该跨会话保存
        activeOperationsTab: activeOperationsTab || null,
        operationsMenuVisible,
      });
    }
  }, [sceneId, visible, sceneInfo, sceneLocationDisplayText, allScenes, activeTab, localPlotDay, pagesNumberInputValue, localSceneCode, activeOperationsTab, operationsMenuVisible, setTabCache, tabId, location.pathname]);


  // handleSceneUpdate 已移至 useSceneUpdate Hook

  // handleElementUpdate 已移至 useElementUpdates Hook

  // 字段更新服务

  // 注意：全局键盘快捷键（回车键、Tab键、方向键）已在 Breakdown.tsx 中统一注册
  // 这里不再重复注册，避免事件监听器重复注册导致事件被处理多次

  // checkDuplicateName 和 duplicateCheckResults 已移至 useElementDuplicateCheck Hook




  // 注意：handleDeleteSingleItem 已移除，删除操作已在 ElementsTable 组件内部处理

  // 操作菜单功能已移至 useElementOperations Hook
  
  // getTotalClipboardCount 已移至 useBatchPasteElements Hook


  // 获取主要角色字符串：从主要角色类型的 elements 中提取元素简称或名称，用/分隔
  // 按 elementNO 排序
  // 使用 effectiveElements 与表格显示一致，避免数据来源不同导致不显示
  const getMainRoles = (): string => {
    const mainRoleItems = effectiveElements.filter(item => item.elementType === ElementType.CAST);
    
    // 按 elementNO 排序
    const sortedMainRoleItems = sortCastItemsByNO(mainRoleItems);
    
    const roleNames = sortedMainRoleItems.map(item => {
      // 优先显示简称，如果没有简称则显示完整名称
      return item.elementAbbr || item.elementName || '';
    }).filter(Boolean);
    
    return roleNames.join(' / ') || '';
  };


  if (!visible) {
    return null;
  }


  // 渲染主要内容
  const renderMainContent = () => (
    <>
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>{t('common.loading')}</span>
        </div>
      ) : error ? (
        <div className="error-container">
          <div className="error-message">
            <span>❌ {error}</span>
            <button className="btn-retry" onClick={() => window.location.reload()}>{t('common.retry')}</button>
          </div>
        </div>
      ) : sceneInfo ? (
        <>
              {/* 场次详情区域 - 内联 SceneInfo 组件 */}
              <div className="scene-details-section">
                {/* 左右两个容器，宽度比例 4:6 */}
                <div 
                  ref={sceneDetailsContainerRef}
                  className="scene-details-container"
                >
                  {/* 左边容器：40% */}
                  <div className="scene-details-left">
                    {/* 第一行：内外、日夜 */}
                    <div className="scene-details-row">
                      <div className="scene-detail-item scene-detail-item-1A">
                        <label>{t('sceneDetails.labelInteriorExterior')}</label>
                        <div className="scene-detail-input-container">
                          <button
                            ref={(el) => {
                              interiorExteriorButtonRef.current = el;
                              fieldRefs.current['sceneInteriorExterior'] = el;
                              if (el) {
                                // 绑定更新函数到WeakMap
                                updateHandlersMap.set(el, async (val: any) => {
                                  handleSceneUpdate('sceneInteriorExterior', val as SceneInteriorExterior);
                                  flushSceneUpdate();
                                });
                                // 存储 openMenu 方法到元素属性（供键盘导航使用）
                                (el as any).__openMenu = () => {
                                  setDayNightMenuVisible(false);
                                  setInteriorExteriorMenuVisible(true);
                                };
                              }
                            }}
                            className="scene-detail-select-button"
                            data-field="sceneInteriorExterior"
                            data-scene-id={sceneId}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDayNightMenuVisible(false);
                              setInteriorExteriorMenuVisible((prev) => !prev);
                            }}
                          >
                            {sceneInfo.sceneInteriorExterior === SceneInteriorExterior.INTERIOR
                              ? t('sceneDetails.ioInterior')
                              : sceneInfo.sceneInteriorExterior === SceneInteriorExterior.EXTERIOR
                              ? t('sceneDetails.ioExterior')
                              : sceneInfo.sceneInteriorExterior === SceneInteriorExterior.INTERIOR_EXTERIOR
                              ? t('sceneDetails.ioInteriorExterior')
                              : '\u00A0'}
                          </button>
                          {interiorExteriorMenuVisible && interiorExteriorMenuPosition && (
                            <DropdownMenu
                              items={[
                                { id: SceneInteriorExterior.INTERIOR, label: t('sceneDetails.ioInterior') },
                                { id: SceneInteriorExterior.EXTERIOR, label: t('sceneDetails.ioExterior') },
                                { id: SceneInteriorExterior.INTERIOR_EXTERIOR, label: t('sceneDetails.ioInteriorExterior') },
                              ]}
                              visible={interiorExteriorMenuVisible}
                              onClose={() => {
                                setInteriorExteriorMenuVisible(false);
                              }}
                              onSelect={(item) => {
                                const newValue = item.id as SceneInteriorExterior;
                                handleSceneUpdate('sceneInteriorExterior', newValue);
                                flushSceneUpdate();
                                setInteriorExteriorMenuVisible(false);
                                if (interiorExteriorButtonRef.current) {
                                  interiorExteriorButtonRef.current.blur();
                                }
                              }}
                              position={{
                                top: interiorExteriorMenuPosition.top,
                                left: interiorExteriorMenuPosition.left
                              }}
                              width={interiorExteriorMenuPosition.width}
                              selectedValue={sceneInfo.sceneInteriorExterior || null}
                              useFixedPosition
                              portalToBody
                              className="scene-detail-field-dropdown"
                            />
                          )}
                        </div>
                      </div>
                      <div className="scene-detail-item scene-detail-item-1B">
                        <label>{t('sceneDetails.labelDayNight')}</label>
                        <div className="scene-detail-input-container">
                          <button
                            ref={(el) => {
                              dayNightButtonRef.current = el;
                              fieldRefs.current['sceneDayNight'] = el;
                              if (el) {
                                // 绑定更新函数到WeakMap
                                updateHandlersMap.set(el, async (val: any) => {
                                  handleSceneUpdate('sceneDayNight', val as SceneDayNight);
                                  flushSceneUpdate();
                                });
                                // 存储 openMenu 方法到元素属性（供键盘导航使用）
                                (el as any).__openMenu = () => {
                                  setInteriorExteriorMenuVisible(false);
                                  setDayNightMenuVisible(true);
                                };
                              }
                            }}
                            className="scene-detail-select-button"
                            data-field="sceneDayNight"
                            data-scene-id={sceneId}
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              setInteriorExteriorMenuVisible(false);
                              setDayNightMenuVisible((prev) => !prev);
                            }}
                          >
                            {sceneInfo.sceneDayNight === SceneDayNight.MORNING
                              ? t('sceneDetails.dnMorning')
                              : sceneInfo.sceneDayNight === SceneDayNight.DAY
                              ? t('sceneDetails.dnDay')
                              : sceneInfo.sceneDayNight === SceneDayNight.DUSK
                              ? t('sceneDetails.dnDusk')
                              : sceneInfo.sceneDayNight === SceneDayNight.NIGHT
                              ? t('sceneDetails.dnNight')
                              : '\u00A0'}
                          </button>
                          {dayNightMenuVisible && dayNightMenuPosition && (
                            <DropdownMenu
                              items={[
                                {
                                  id: SceneDayNight.MORNING,
                                  label: t('sceneDetails.dnMorning'),
                                },
                                {
                                  id: SceneDayNight.DAY,
                                  label: t('sceneDetails.dnDay'),
                                },
                                {
                                  id: SceneDayNight.DUSK,
                                  label: t('sceneDetails.dnDusk'),
                                },
                                {
                                  id: SceneDayNight.NIGHT,
                                  label: t('sceneDetails.dnNight'),
                                },
                              ]}
                              visible={dayNightMenuVisible}
                              onClose={() => {
                                setDayNightMenuVisible(false);
                              }}
                              onSelect={(item) => {
                                const newValue = item.id as SceneDayNight;
                                handleSceneUpdate('sceneDayNight', newValue);
                                flushSceneUpdate();
                                setDayNightMenuVisible(false);
                                if (dayNightButtonRef.current) {
                                  dayNightButtonRef.current.blur();
                                }
                              }}
                              position={{
                                top: dayNightMenuPosition.top,
                                left: dayNightMenuPosition.left
                              }}
                              width={dayNightMenuPosition.width}
                              selectedValue={sceneInfo.sceneDayNight || null}
                              useFixedPosition
                              portalToBody
                              className="scene-detail-field-dropdown"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 第二行：剧情日、页数 */}
                    <div className="scene-details-row">
                      <div className="scene-detail-item scene-detail-item-1A">
                        <label>{t('sceneDetails.labelPlotDay')}</label>
                        <input
                          ref={(el) => {
                            fieldRefs.current['scenePlotDay'] = el;
                            if (el) {
                              updateHandlersMap.set(el, async (val: any) => {
                                const key = `scenePlotDay`;
                                const localValue = localInputValues.get(key);
                                const finalValue = localValue !== undefined ? localValue : val;
                                const plotDayValue = finalValue.trim();
                                
                                // 提交更新和同步
                                if (plotDayValue === '') {
                                  await handleSceneUpdate('scenePlotDay', undefined);
                                  await syncToTable('scenePlotDay', undefined);
                                } else {
                                  await handleSceneUpdate('scenePlotDay', plotDayValue);
                                  await syncToTable('scenePlotDay', plotDayValue);
                                }
                                await flushSceneUpdate();
                                
                                // 清除本地状态
                                setLocalInputValues(prev => {
                                  const next = new Map(prev);
                                  next.delete(key);
                                  return next;
                                });
                              });
                            }
                            // 合并 createFieldHandlers 的 ref
                            const handlersRef = createFieldHandlers({
                              // 注意：双击时会通过 __doubleClickX 定位光标，不会全选文本
                              // Enter 键进入编辑时才会全选文本
                              selectText: true,
                            }).ref;
                            if (handlersRef && typeof handlersRef === 'function') {
                              handlersRef(el);
                            }
                          }}
                          type="text"
                          data-scene-id={sceneId}
                          data-field="scenePlotDay"
                          className="scene-detail-input"
                          tabIndex={-1}
                          data-row={fieldPositionMap.get('scenePlotDay')?.row}
                          data-col={fieldPositionMap.get('scenePlotDay')?.col}
                          {...(() => {
                            const handlers = createFieldHandlers({
                              // 注意：双击时会通过 __doubleClickX 定位光标，不会全选文本
                              // Enter 键进入编辑时才会全选文本
                              selectText: true,
                            });
                            // 移除 ref，因为我们已经在 ref 回调中手动调用了
                            const { ref, ...rest } = handlers;
                            return rest;
                          })()}
                          onBlur={(e) => {
                            const key = `scenePlotDay`;
                            const localValue = localInputValues.get(key);
                            const finalValue = localValue !== undefined ? localValue : e.target.value;
                            const plotDayValue = finalValue.trim();
                            
                            // 更新输入框的值（如果使用了本地值）
                            if (localValue !== undefined) {
                              (e.target as HTMLInputElement).value = plotDayValue;
                            }
                            
                            // 调用原有的 blur 处理函数（会处理提交）
                            handlePlotDayBlur(e);
                            
                            // 同步到场次表
                            if (plotDayValue === '') {
                              syncToTable('scenePlotDay', undefined);
                            } else {
                              syncToTable('scenePlotDay', plotDayValue);
                            }
                            
                            // 清除本地状态
                            setLocalInputValues(prev => {
                              const next = new Map(prev);
                              next.delete(key);
                              return next;
                            });
                          }}
                          value={localInputValues.get('scenePlotDay') !== undefined 
                            ? localInputValues.get('scenePlotDay')! 
                            : (sceneInfo.scenePlotDay !== null && sceneInfo.scenePlotDay !== undefined ? String(sceneInfo.scenePlotDay) : '')}
                          onChange={(e) => {
                            const inputVal = e.target.value;
                            setLocalInputValues(prev => {
                              const next = new Map(prev);
                              next.set('scenePlotDay', inputVal);
                              return next;
                            });
                          }}
                        />
                      </div>
                      <div className="scene-detail-item scene-detail-item-1B">
                        <label>{t('scene.scriptPages')}</label>
                        <input
                          ref={(el) => {
                            fieldRefs.current['scenePagesNumber'] = el;
                            if (el) {
                              updateHandlersMap.set(el, async (val: any) => {
                                // 优先使用本地输入值
                                const localValue = pagesNumberInputValue;
                                const inputVal = localValue !== undefined ? localValue : (typeof val === 'string' ? val : String(val));
                                
                                let processedValue: number | undefined;
                                // 清除本地输入值状态
                                setPagesNumberInputValue(undefined);
                                
                                // 如果值是字符串，需要验证和格式化
                                if (typeof inputVal === 'string') {
                                  const trimmedVal = inputVal.trim();
                                  // 如果输入为空，设置为 undefined（清空字段）
                                  if (trimmedVal === '') {
                                    processedValue = undefined;
                                  } else {
                                    const validatedValue = validateAndFormatPageNumber(trimmedVal);
                                    if (validatedValue === undefined) {
                                      // 如果输入无效，恢复为原始值
                                      processedValue = sceneInfo?.scenePagesNumber;
                                    } else {
                                      processedValue = validatedValue;
                                    }
                                  }
                                } else if (inputVal !== null && inputVal !== undefined) {
                                  // 如果值已经是数字，确保格式正确（最多1位小数）
                                  processedValue = Math.round(Number(inputVal) * 10) / 10;
                                } else {
                                  // 如果值是 null 或 undefined，保持为 undefined（清空字段）
                                  processedValue = undefined;
                                }
                                
                                // 提交更新和同步
                                await handleSceneUpdate('scenePagesNumber', processedValue);
                                await syncToTable('scenePagesNumber', processedValue);
                                await flushSceneUpdate();
                              });
                            }
                            // 合并 createFieldHandlers 的 ref
                            const handlersRef = createFieldHandlers({
                              onEnterEditing: (element) => {
                                // 初始化本地输入值，使用统一的格式化函数（参考场次表的实现）
                                const currentValue = sceneInfo.scenePagesNumber !== null && sceneInfo.scenePagesNumber !== undefined 
                                  ? formatPageNumberDisplay(sceneInfo.scenePagesNumber) 
                                  : '';
                                setPagesNumberInputValue(currentValue);
                              },
                              selectText: true,
                            }).ref;
                            if (handlersRef && typeof handlersRef === 'function') {
                              handlersRef(el);
                            }
                          }}
                          type="text"
                          inputMode="decimal"
                          data-scene-id={sceneId}
                          data-field="scenePagesNumber"
                          className="scene-detail-input"
                          tabIndex={-1}
                          data-row={fieldPositionMap.get('scenePagesNumber')?.row}
                          data-col={fieldPositionMap.get('scenePagesNumber')?.col}
                          {...(() => {
                            const handlers = createFieldHandlers({
                              onEnterEditing: (element) => {
                                // 初始化本地输入值，使用统一的格式化函数（参考场次表的实现）
                                const currentValue = sceneInfo.scenePagesNumber !== null && sceneInfo.scenePagesNumber !== undefined 
                                  ? formatPageNumberDisplay(sceneInfo.scenePagesNumber) 
                                  : '';
                                setPagesNumberInputValue(currentValue);
                              },
                              selectText: true,
                            });
                            // 移除 ref，因为我们已经在 ref 回调中手动调用了
                            const { ref, ...rest } = handlers;
                            return rest;
                          })()}
                          onBlur={(e) => {
                            // 优先使用本地输入值
                            const localValue = pagesNumberInputValue;
                            if (localValue !== undefined) {
                              (e.target as HTMLInputElement).value = localValue;
                            }
                            
                            // 调用原有的 blur 处理函数（会处理提交）
                            handlePagesNumberBlur(e);
                            
                            // 同步到场次表
                            const inputVal = (e.target as HTMLInputElement).value.trim();
                            if (inputVal === '') {
                              syncToTable('scenePagesNumber', undefined);
                            } else {
                              const validatedValue = validateAndFormatPageNumber(inputVal);
                              if (validatedValue !== undefined) {
                                syncToTable('scenePagesNumber', validatedValue);
                              }
                            }
                          }}
                          value={pagesNumberInputValue !== undefined 
                            ? pagesNumberInputValue 
                            : (sceneInfo.scenePagesNumber !== null && sceneInfo.scenePagesNumber !== undefined 
                                ? formatPageNumberDisplay(sceneInfo.scenePagesNumber) 
                                : '')}
                          onChange={(e) => {
                            // 使用统一的输入验证函数，实时阻止非法输入（参考场次表的实现）
                            let inputVal = e.target.value;
                            
                            // 将全角句号转换为半角句号（处理中文输入法下的句号）
                            inputVal = inputVal.replace(/。/g, '.');
                            
                            // 如果转换后值有变化，更新输入框的值
                            if (inputVal !== e.target.value) {
                              e.target.value = inputVal;
                            }
                            
                            // 验证输入是否合法
                            if (validatePageNumberInput(inputVal)) {
                              // 存储到本地状态，允许不完整的输入（如 "1." 或 "0"）
                              // 不在 onChange 中调用 handleSceneUpdate 和 syncToTable，等待 onBlur 或 ref 时提交
                              setPagesNumberInputValue(inputVal);
                            } else {
                              // 如果验证失败，恢复输入框的值（防止光标位置错乱）
                              // 获取当前显示值
                              const currentDisplayValue = pagesNumberInputValue !== undefined
                                ? pagesNumberInputValue
                                : (sceneInfo.scenePagesNumber !== null && sceneInfo.scenePagesNumber !== undefined 
                                    ? formatPageNumberDisplay(sceneInfo.scenePagesNumber) 
                                    : '');
                              
                              // 恢复输入框的值和光标位置
                              const input = e.target;
                              const cursorPosition = input.selectionStart || 0;
                              input.value = currentDisplayValue;
                              
                              // 恢复光标位置（如果光标在末尾，保持在末尾）
                              setTimeout(() => {
                                const newPosition = Math.min(cursorPosition, currentDisplayValue.length);
                                input.setSelectionRange(newPosition, newPosition);
                              }, 0);
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* 第三行：拍摄地点 */}
                    <div className="scene-details-row">
                      <div className="scene-detail-item scene-detail-item-2">
                        <label>{t('sceneDetails.labelShootingLocation')}</label>
                        <div className="scene-detail-input-container">
                          <input
                            ref={(el) => {
                              fieldRefs.current['sceneLocation'] = el;
                              if (el) {
                                updateHandlersMap.set(el, async (val: any) => {
                                  const key = `sceneLocation`;
                                  const localValue = localInputValues.get(key);
                                  const finalValue = localValue !== undefined ? localValue : val;
                                  const locationValue = finalValue.trim();
                                  
                                  // 提交更新和同步
                                  await handleSceneUpdate('sceneLocation', locationValue);
                                  await syncToTable('sceneLocation', locationValue);
                                  
                                  // 清除本地状态
                                  setLocalInputValues(prev => {
                                    const next = new Map(prev);
                                    next.delete(key);
                                    return next;
                                  });
                                });
                                // 合并 createFieldHandlers 的 ref
                                const handlersRef = createFieldHandlers({
                                  selectText: true,
                                }).ref;
                                if (handlersRef && typeof handlersRef === 'function') {
                                  handlersRef(el);
                                }
                              }
                            }}
                            type="text"
                            data-scene-id={sceneId}
                            data-field="sceneLocation"
                            className="scene-detail-input"
                            tabIndex={-1}
                            data-row={fieldPositionMap.get('sceneLocation')?.row}
                            data-col={fieldPositionMap.get('sceneLocation')?.col}
                            {...(() => {
                              const handlers = createFieldHandlers({
                                selectText: true,
                              });
                              // 移除 ref，因为我们已经在 ref 回调中手动调用了
                              const { ref, ...rest } = handlers;
                              return rest;
                            })()}
                            onBlur={async (e) => {
                              const key = `sceneLocation`;
                              const localValue = localInputValues.get(key);
                              const finalValue = localValue !== undefined ? localValue : e.target.value;
                              const locationValue = finalValue.trim();
                              
                              // 更新输入框的值（如果使用了本地值）
                              if (localValue !== undefined) {
                                (e.target as HTMLInputElement).value = locationValue;
                              }
                              
                              // 提交更新和同步
                              handleSceneUpdate('sceneLocation', locationValue);
                              syncToTable('sceneLocation', locationValue);
                              await flushSceneUpdate();
                              
                              // 清除本地状态
                              setLocalInputValues(prev => {
                                const next = new Map(prev);
                                next.delete(key);
                                return next;
                              });
                            }}
                            value={localInputValues.get('sceneLocation') !== undefined 
                              ? localInputValues.get('sceneLocation')! 
                              : (sceneInfo.sceneLocation || '')}
                            onChange={(e) => {
                              const inputVal = e.target.value;
                              setLocalInputValues(prev => {
                                const next = new Map(prev);
                                next.set('sceneLocation', inputVal);
                                return next;
                              });
                            }}
                          />
                          <InputRecommendNEW
                            fieldType="sceneLocation"
                            sceneId={sceneId}
                            dataSource={allScenes.map(s => s.sceneLocation)}
                            variant="detail"
                          />
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* 右边容器：60% */}
                  <div className="scene-details-right">
                    {/* 第一行：场景 */}
                    <div className="scene-details-row">
                      <div className="scene-detail-item scene-detail-item-3">
                        <label>{t('scene.set')}</label>
                        <div className="scene-detail-input-container">
                          <input
                            ref={(el) => {
                              fieldRefs.current['sceneSet'] = el;
                              if (el) {
                                updateHandlersMap.set(el, async (val: any) => {
                                  const key = `sceneSet`;
                                  const localValue = localInputValues.get(key);
                                  const finalValue = localValue !== undefined ? localValue : val;
                                  const setValue = finalValue.trim();
                                  
                                  // 提交更新和同步
                                  await handleSceneUpdate('sceneSet', setValue);
                                  await syncToTable('sceneSet', setValue);
                                  
                                  // 当场景字段完成输入时，检查并自动填充拍摄地点
                                  if (setValue) {
                                    checkAndAutoFillLocation(setValue);
                                  }
                                  
                                  // 清除本地状态
                                  setLocalInputValues(prev => {
                                    const next = new Map(prev);
                                    next.delete(key);
                                    return next;
                                  });
                                });
                                // 合并 createFieldHandlers 的 ref
                                const handlersRef = createFieldHandlers({
                                  selectText: true,
                                }).ref;
                                if (handlersRef && typeof handlersRef === 'function') {
                                  handlersRef(el);
                                }
                              }
                            }}
                            type="text"
                            data-scene-id={sceneId}
                            data-field="sceneSet"
                            className="scene-detail-input"
                            tabIndex={-1}
                            data-row={fieldPositionMap.get('sceneSet')?.row}
                            data-col={fieldPositionMap.get('sceneSet')?.col}
                            {...(() => {
                              const handlers = createFieldHandlers({
                                selectText: true,
                              });
                              // 移除 ref，因为我们已经在 ref 回调中手动调用了
                              const { ref, ...rest } = handlers;
                              return rest;
                            })()}
                            onBlur={async (e) => {
                              const key = `sceneSet`;
                              const localValue = localInputValues.get(key);
                              const finalValue = localValue !== undefined ? localValue : e.target.value;
                              const setValue = finalValue.trim();
                              
                              // 更新输入框的值（如果使用了本地值）
                              if (localValue !== undefined) {
                                (e.target as HTMLInputElement).value = setValue;
                              }
                              
                              // 提交更新和同步
                              handleSceneUpdate('sceneSet', setValue);
                              syncToTable('sceneSet', setValue);
                              await flushSceneUpdate();
                              
                              // 当场景字段完成输入时，检查并自动填充拍摄地点
                              if (setValue) {
                                checkAndAutoFillLocation(setValue);
                              }
                              
                              // 清除本地状态
                              setLocalInputValues(prev => {
                                const next = new Map(prev);
                                next.delete(key);
                                return next;
                              });
                            }}
                            value={localInputValues.get('sceneSet') !== undefined 
                              ? localInputValues.get('sceneSet')! 
                              : (sceneInfo.sceneSet || '')}
                            onChange={(e) => {
                              const inputVal = e.target.value;
                              setLocalInputValues(prev => {
                                const next = new Map(prev);
                                next.set('sceneSet', inputVal);
                                return next;
                              });
                            }}
                          />
                          <InputRecommendNEW
                            fieldType="sceneSet"
                            sceneId={sceneId}
                            dataSource={allScenes.map(s => s.sceneSet)}
                            variant="detail"
                            onAutoFillLocation={(sceneId, sceneSetValue) => checkAndAutoFillLocation(sceneSetValue)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 第二行：剧情 */}
                    <div className="scene-details-row">
                      <div className="scene-detail-item scene-detail-item-3">
                        <label>{t('scene.plot')}</label>
                        <input
                          ref={(el) => {
                            fieldRefs.current['scenePlot'] = el;
                            if (el) {
                              updateHandlersMap.set(el, async (val: any) => {
                                const key = `scenePlot`;
                                const localValue = localInputValues.get(key);
                                const finalValue = localValue !== undefined ? localValue : val;
                                const plotValue = finalValue.trim();
                                
                                // 提交更新和同步
                                await handleSceneUpdate('scenePlot', plotValue);
                                await syncToTable('scenePlot', plotValue);
                                await flushSceneUpdate();
                                
                                // 清除本地状态
                                setLocalInputValues(prev => {
                                  const next = new Map(prev);
                                  next.delete(key);
                                  return next;
                                });
                              });
                              // 合并 createFieldHandlers 的 ref
                              const handlersRef = createFieldHandlers({
                                selectText: true,
                              }).ref;
                              if (handlersRef && typeof handlersRef === 'function') {
                                handlersRef(el);
                              }
                            }
                          }}
                          type="text"
                          data-field="scenePlot"
                          className="scene-detail-input"
                          tabIndex={-1}
                          data-row={fieldPositionMap.get('scenePlot')?.row}
                          data-col={fieldPositionMap.get('scenePlot')?.col}
                          {...(() => {
                            const handlers = createFieldHandlers({
                              selectText: true,
                            });
                            // 移除 ref，因为我们已经在 ref 回调中手动调用了
                            const { ref, ...rest } = handlers;
                            return rest;
                          })()}
                          onBlur={(e) => {
                            const key = `scenePlot`;
                            const localValue = localInputValues.get(key);
                            const finalValue = localValue !== undefined ? localValue : e.target.value;
                            const plotValue = finalValue.trim();
                            
                            // 更新输入框的值（如果使用了本地值）
                            if (localValue !== undefined) {
                              (e.target as HTMLInputElement).value = plotValue;
                            }
                            
                            // 先提交 scenePlot 更新和同步，确保 pending 中已有 scenePlot，
                            // 再执行 handlePlotBlur（会更新 scenePlotSummary 并 flush），
                            // 避免 flush 时缓存覆盖导致表格不同步
                            handleSceneUpdate('scenePlot', plotValue);
                            syncToTable('scenePlot', plotValue);
                            handlePlotBlur(e);
                            
                            // 清除本地状态
                            setLocalInputValues(prev => {
                              const next = new Map(prev);
                              next.delete(key);
                              return next;
                            });
                          }}
                          value={localInputValues.get('scenePlot') !== undefined 
                            ? localInputValues.get('scenePlot')! 
                            : (sceneInfo.scenePlot || '')}
                          onChange={(e) => {
                            const inputVal = e.target.value;
                            setLocalInputValues(prev => {
                              const next = new Map(prev);
                              next.set('scenePlot', inputVal);
                              return next;
                            });
                          }}
                        />
                      </div>
                    </div>

                    {/* 第三行：日历摘要 */}
                    <div className="scene-details-row">
                      <div className="scene-detail-item scene-detail-item-3">
                        <label>{t('scene.plotSummary')}</label>
                        <input
                          ref={(el) => {
                            fieldRefs.current['scenePlotSummary'] = el;
                            if (el) {
                              updateHandlersMap.set(el, async (val: any) => {
                                const key = `scenePlotSummary`;
                                const localValue = localInputValues.get(key);
                                const finalValue = localValue !== undefined ? localValue : val;
                                const plotSummaryValue = finalValue.trim();
                                
                                // 提交更新和同步
                                await handleSceneUpdate('scenePlotSummary', plotSummaryValue);
                                await flushSceneUpdate();
                                await syncToTable('scenePlotSummary', plotSummaryValue);
                                
                                // 清除本地状态
                                setLocalInputValues(prev => {
                                  const next = new Map(prev);
                                  next.delete(key);
                                  return next;
                                });
                              });
                              // 合并 createFieldHandlers 的 ref
                              const handlersRef = createFieldHandlers({
                                selectText: true,
                              }).ref;
                              if (handlersRef && typeof handlersRef === 'function') {
                                handlersRef(el);
                              }
                            }
                          }}
                          type="text"
                          data-scene-id={sceneId}
                          data-field="scenePlotSummary"
                          className="scene-detail-input"
                          tabIndex={-1}
                          data-row={fieldPositionMap.get('scenePlotSummary')?.row}
                          data-col={fieldPositionMap.get('scenePlotSummary')?.col}
                          {...(() => {
                            const handlers = createFieldHandlers({
                              selectText: true,
                            });
                            // 移除 ref，因为我们已经在 ref 回调中手动调用了
                            const { ref, ...rest } = handlers;
                            return rest;
                          })()}
                          onBlur={(e) => {
                            const key = `scenePlotSummary`;
                            const localValue = localInputValues.get(key);
                            const finalValue = localValue !== undefined ? localValue : e.target.value;
                            const plotSummaryValue = finalValue.trim();
                            
                            // 更新输入框的值（如果使用了本地值）
                            if (localValue !== undefined) {
                              (e.target as HTMLInputElement).value = plotSummaryValue;
                            }
                            
                            // 调用原有的 blur 处理函数
                            handlePlotSummaryBlur();
                            
                            // 提交更新和同步（flushSceneUpdate 更新缓存，通知日历等订阅者）
                            handleSceneUpdate('scenePlotSummary', plotSummaryValue);
                            flushSceneUpdate().then(() => syncToTable('scenePlotSummary', plotSummaryValue));
                            
                            // 清除本地状态
                            setLocalInputValues(prev => {
                              const next = new Map(prev);
                              next.delete(key);
                              return next;
                            });
                          }}
                          value={localInputValues.get('scenePlotSummary') !== undefined 
                            ? localInputValues.get('scenePlotSummary')! 
                            : (sceneInfo.scenePlotSummary || '')}
                          onChange={(e) => {
                            const inputVal = e.target.value;
                            setLocalInputValues(prev => {
                              const next = new Map(prev);
                              next.set('scenePlotSummary', inputVal);
                              return next;
                            });
                          }}
                        />
                      </div>
                    </div>

                  </div>
                </div>

                {/* 主要角色：独立一行，占满整行 */}
                <div className="scene-details-row scene-main-roles-row">
                  <div className="scene-detail-item scene-main-roles-item">
                    <label>{t('elementType.CAST')}</label>
                    <input
                      type="text"
                      value={getMainRoles()}
                      readOnly
                      tabIndex={-1}
                      className="scene-detail-input scene-main-roles-input"
                    />
                  </div>
                </div>
              </div>

              {/* 场次元素区域 - 内联 ElementTabs 和 ElementsTable */}
              <div className="elements-section" ref={elementsSectionRef}>
                <div className="elements-content">
                  {/* 左侧标签 - 内联 ElementTabs */}
                  <div className="elements-tabs">
                    {tabs.map(tab => (
                      <button
                        key={tab}
                        className={`element-tab ${activeTab === tab ? 'active' : ''}`}
                        disabled={!canEdit}
                        onClick={() => {
                          if (!canEdit) return;
                          setActiveTab(tab);
                          handleOpenElementSearchInternal(tab);
                        }}
                        tabIndex={-1}
                      >
                        <div className="element-tab-content">
                          <div className="element-tab-content-left">
                            {React.createElement(elementTypeSvgIcons[tab], {
                              className: 'element-tab-icon',
                              'aria-label': String(t(`sceneDetails.itemTabs.${tab}`)),
                            })}
                            <span>{String(t(`sceneDetails.itemTabs.${tab}`))}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* ElementSearch 弹窗 - 仅在非外部模式下显示 */}
                  {!externalElementSearch && elementSearchVisible && effectiveBreakdownId && (
                    <ElementSearch
                      value={null}
                      onChange={handleElementSearchChangeInternal}
                      visible={elementSearchVisible}
                      onClose={handleCloseElementSearchInternal}
                      breakdownID={effectiveBreakdownId}
                      currentElementType={elementSearchType}
                      sceneID={sceneId}
                      selectedElementIds={selectedElementIds}
                    />
                  )}

                  {/* 右侧表格 - 内联 ElementsTable */}
                  <div className="elements-table">
                    <div 
                      ref={elementsContainerRef}
                      className="element-table"
                      style={{
                        height: '100%',
                        overflowY: elementLayoutStats.needsInnerScroll ? 'auto' : 'hidden',
                      }}
                    >
                      <div className="element-table-row">
                        {elementTableColumns.map((column, columnIndex) => (
                          <div key={columnIndex} className="element-table-column">
                            {column.map((item, itemIndex) => {
                              // 判断是否是分类头部
                              if ('isCategoryHeader' in item && item.isCategoryHeader) {
                                const categoryType = item.categoryType;
                                // 使用 effectiveElements 来计算数量，而不是 elements
                                const itemCount = effectiveElements.filter(item => item.elementType === categoryType).length;
                                const hasItems = itemCount > 0;
                                const canCopy = canEdit &&
                                  handleCopyCategoryItems !== undefined && !hideCategoryHeaderCopy;
                                const canDelete = canEdit && handleClearCategoryItems !== undefined;
                                
                                return (
                                  <div 
                                    key={`category-${categoryType}-${columnIndex}-${itemIndex}`}
                                    className="element-category-header"
                                  >
                                    {React.createElement(elementTypeSvgIcons[categoryType], {
                                      className: 'element-category-icon',
                                      'aria-label': String(t(`sceneDetails.itemTabs.${categoryType}`))
                                    })}
                                    <span className="element-category-name">
                                      {String(t(`sceneDetails.itemTabs.${categoryType}`))}
                                    </span>
                                    <div className="element-category-actions">
                                      {canCopy && (
                                        <button
                                          className="element-category-action-btn element-category-copy-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (hasItems && handleCopyCategoryItems) {
                                              handleCopyCategoryItems(categoryType);
                                            }
                                          }}
                                          disabled={!hasItems}
                                          title={hasItems ? t('sceneDetails.duplicateAllElements') : t('sceneDetails.noElements')}
                                          tabIndex={-1}
                                        >
                                          <CopyIcon className="element-category-action-icon" />
                                        </button>
                                      )}
                                      {canDelete && (
                                        <button
                                          ref={deleteButtonRefs.current[categoryType]}
                                          className="element-category-action-btn element-category-delete-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (hasItems) {
                                              setPendingDeleteCategory(categoryType);
                                              setDeleteDialogVisible(true);
                                            }
                                          }}
                                          disabled={!hasItems}
                                          title={hasItems ? t('sceneDetails.clearSelectedElements') : t('sceneDetails.noElements')}
                                          tabIndex={-1}
                                        >
                                          <DeleteIcon className="element-category-action-icon" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                              
                              // 正常的场次元素
                              const element = item as ElementWithUserNames;
                              const isEditing = editingItem === element.elementID;
                              const isExtras = element.elementType === ElementType.BACKGROUND;
                              const isFeaturedRole = element.elementType === ElementType.FEATURED;
                              const isActionActor = element.elementType === ElementType.STUNT_PLAYERS;
                              const isAnimal = element.elementType === ElementType.ANIMALS;
                              const isVehicle = element.elementType === ElementType.VEHICLES;
                              const showQuantity = isExtras || isFeaturedRole || isActionActor || isAnimal || isVehicle;
                              
                              return (
                                <div 
                                  key={element.elementID} 
                                  className="element-cell"
                                >
                                  <div className="element-content">
                                    {/* 输入框区域 */}
                                    <div className="element-inputs">
                                      {/* 元素名称输入框容器 */}
                                      <div className={`element-name-input-wrapper ${duplicateNameMap.get(element.elementID) ? 'has-duplicate' : ''}`}>
                                        <input
                                          ref={(el) => {
                                            if (el) {
                                              elementsFieldRefs.current.set(`${element.elementID}-name`, el);
                                              updateHandlersMap.set(el, async (val: any) => {
                                                await handleUpdateElement(element.elementID, 'elementName', val);
                                              });
                                              // 设置 selectText 属性，以便回车键进入编辑状态时全选文本
                                              (el as any).__selectText = true;
                                            }
                                          }}
                                          type="text"
                                          data-item-id={element.elementID}
                                          data-field="name"
                                          className="element-input element-name-input"
                                          tabIndex={-1}
                                          value={isEditing && editingField === 'name' ? (editValues[`${element.elementID}-name`] || '') : element.elementName}
                                          onChange={(e) => handleElementInputChange('name', e.target.value, element.elementID)}
                                          onBlur={createElementFieldBlurHandler}
                                          onFocus={(e) => {
                                            if (!(isEditing && editingField === 'name')) {
                                              handleElementInputActivate(e.target as HTMLElement, element.elementID, 'name');
                                            }
                                            // 注意：状态管理已由统一导航系统处理
                                          }}
                                          onClick={(e) => {
                                            if (!(isEditing && editingField === 'name')) {
                                              handleElementInputActivate(e.target as HTMLElement, element.elementID, 'name');
                                            }
                                          }}
                                          autoFocus={isEditing && editingField === 'name'}
                                        />
                                        {/* 重复警告图标 */}
                                        {duplicateNameMap.get(element.elementID) && (
                                          <WarningIcon
                                            className="element-name-duplicate-icon"
                                            aria-label={t('sceneDetails.duplicateAriaLabel')}
                                          />
                                        )}
                  </div>
                                      
                                      {/* 主要角色类型的简称展示（只读） */}
                                      {element.elementType === ElementType.CAST && (
                                        <span
                                          className="element-description-display"
                                          title={element.elementAbbr || ''}
                                        >
                                          {element.elementAbbr || ''}
                                        </span>
                                      )}
                                      
                                      {/* 群众演员、特约演员、动作演员、动物、车辆类型的数量输入框 */}
                                      {showQuantity && (
                                        <input
                                          ref={(el) => {
                                            if (el) {
                                              elementsFieldRefs.current.set(`${element.elementID}-quantity`, el);
                                              updateHandlersMap.set(el, async (val: any) => {
                                                const numVal = val === '' || val === null || val === undefined ? 1 : parseInt(val);
                                                await handleUpdateElement(element.elementID, 'elementQtyInScene', numVal || 1);
                                              });
                                              // 设置 selectText 属性，以便回车键进入编辑状态时全选文本
                                              (el as any).__selectText = true;
                                            }
                                          }}
                                          type="text"
                                          inputMode="numeric"
                                          pattern="[1-9][0-9]*"
                                          data-item-id={element.elementID}
                                          data-field="quantity"
                                          className="element-input element-quantity-input"
                                          tabIndex={-1}
                                          value={isEditing && editingField === 'quantity' 
                                            ? (editValues[`${element.elementID}-quantity`] ?? '') 
                                            : (element.elementQtyInScene?.toString() || '1')}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || /^\d+$/.test(value)) {
                                              handleElementInputChange('quantity', value, element.elementID);
                                            }
                                          }}
                                          onBlur={createElementFieldBlurHandler}
                                          onFocus={(e) => {
                                            const inputEl = e.target as HTMLInputElement;
                                            if (editingItem === element.elementID && editingField === 'quantity') {
                                              inputEl.select();
                                            } else {
                                              handleElementInputActivate(inputEl, element.elementID, 'quantity');
                                            }
                                            // 注意：状态管理已由统一导航系统处理
                                          }}
                                          onClick={(e) => {
                                            if (!(isEditing && editingField === 'quantity')) {
                                              handleElementInputActivate(e.target as HTMLElement, element.elementID, 'quantity');
                                            }
                                          }}
                                          autoFocus={isEditing && editingField === 'quantity'}
                                        />
                                      )}
                                    </div>
                                    
                                    {/* 移除按钮 */}
                                    {canEdit && <RemoveIcon
                                      className="element-delete-btn"
                                      aria-label="移除"
                                      onClick={() => handleDeleteElement(element.elementID)}
                                      tabIndex={-1}
                                      role="button"
                                    />}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      
                      {/* 删除确认对话框 */}
                      {pendingDeleteCategory !== null && deleteButtonRefs.current[pendingDeleteCategory] && (() => {
                        const hasSceneCode = (sceneInfo?.sceneCode || '').trim() !== '';
                        const categoryLabel = String(t(`sceneDetails.itemTabs.${pendingDeleteCategory}`));
                        
                        const message = hasSceneCode
                          ? t('sceneDetails.confirmClearWithCode', { sceneCode: sceneInfo?.sceneCode, category: categoryLabel })
                          : t('sceneDetails.confirmClearWithoutScene', { category: categoryLabel });
                        
                        return (
                          <ButtonDialog
                            visible={deleteDialogVisible}
                            onClose={() => {
                              setDeleteDialogVisible(false);
                              setPendingDeleteCategory(null);
                            }}
                            onConfirm={async () => {
                              if (pendingDeleteCategory !== null && handleClearCategoryItems) {
                                await handleClearCategoryItems(pendingDeleteCategory);
                              }
                              setDeleteDialogVisible(false);
                              setPendingDeleteCategory(null);
                            }}
                            message={message}
                            confirmText="确认"
                            cancelText="取消"
                            buttonRef={deleteButtonRefs.current[pendingDeleteCategory] as React.RefObject<HTMLElement>}
                            align="right"
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}

    </>
  );

  // 直接返回场次内容，不再使用抽屉模式
  return (
    <div className="scene-container">
      {/* 场次头部 */}
      <div className="scene-header">
        <div className="scene-header-left">
          
          <h3 className="scene-title">
            {t('sceneDetails.sceneCodeHeading')}
            {t('sceneDetails.sceneCodeTitleColon')}
            <span className="scene-code-container">
              {isEditingSceneCode ? (
                <input
                  type="text"
                  value={localSceneCode}
                  onChange={(e) => setLocalSceneCode(e.target.value)}
                  autoFocus
                  onBlur={handleSceneCodeBlur}
                  onKeyDown={handleSceneCodeKeyDown}
                  className="scene-detail-input scene-code-input"
                  data-skip-global-handler="true" // 标识：跳过全局键盘事件处理
                  tabIndex={-1}
                />
              ) : (
                <span
                  className="scene-code-display"
                  onClick={() => {
                    setIsEditingSceneCode(true);
                  }}
                  title={(t('common.clickToEdit') as string) || ''}
                >
                  {sceneInfo?.sceneCode || ''}
                </span>
              )}
            </span>
          </h3>
          {/* Scene-Summary-Info 元素 */}
          {!hideSummaryInfo && (
            <div className="scene-summary-info">
                {(sceneOrder !== undefined && totalScenes !== undefined) && (

                    <span className="scene-summary-item">
                      {t('sceneDetails.sceneOfTotal', { current: sceneOrder, total: totalScenes })}{t('common.pipeSeparator')}
                    </span> 

                )}
                {(pagesUpToScene !== undefined && totalPages !== undefined) && (

                    <span className="scene-summary-item">
                      {t('sceneDetails.pageOfTotal', { current: formatPageNumberDisplay(pagesUpToScene), total: formatPageNumberDisplay(totalPages) })}
                    </span>

                )}
            </div>
          )}

        </div>
        <div className="scene-header-actions">
          {!hideHeaderActions && canEdit && (
            <>
              <div className="scene-paste-btn-group">
                <button 
                  type="button"
                  className="scene-header-action-btn"
                  tabIndex={-1}
                  disabled={clipboardCount === 0 && effectiveElements.length === 0}
                  onClick={async () => {
                    if (clipboardCount > 0) {
                      const result = await handleBatchPaste();
                      if (!result.success && result.error) {
                        setAlertDialogContent(result.error);
                        setAlertDialogVisible(true);
                      }
                    } else if (effectiveElements.length > 0) {
                      setClipboardFromFullScene(effectiveElements);
                    }
                  }}
                >
                  <span>
                    {clipboardCount > 0
                      ? t('sceneDetails.pasteElements', { count: clipboardCount })
                      : t('sceneDetails.copyAllElements')}
                  </span>
                </button>
                {clipboardCount > 0 && (
                  <button
                    type="button"
                    className="scene-paste-clear-btn"
                    tabIndex={-1}
                    onClick={() => clearClipboard()}
                    aria-label={t('sceneDetails.cancelPendingPaste')}
                  >
                    <CloseIcon className="scene-paste-clear-icon" aria-hidden />
                  </button>
                )}
              </div>
              <button 
                className="scene-header-action-btn"
                tabIndex={-1}
                onClick={async () => {
                  if (isCopying) return;
                  setIsCopying(true);
                  try {
                    await onCopyScene?.(sceneId);
                  } finally {
                    setIsCopying(false);
                  }
                }}
                disabled={isCopying}
              >
                <span>{isCopying ? t('sceneDetails.duplicating') : t('sceneDetails.duplicate')}</span>
              </button>
              <button 
                className="scene-header-action-btn"
                tabIndex={-1}
                onClick={async () => {
                  if (isDeleting) return;
                  setIsDeleting(true);
                  try {
                    await onDeleteScene?.(sceneId);
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
              >
                <span>{isDeleting ? t('sceneDetails.deleting') : t('sceneDetails.delete')}</span>
              </button>
            </>
          )}
          {showCloseButton && (
            <button 
              className="scene-close-btn"
              tabIndex={-1}
              onClick={onClose}
              aria-label={t('sceneDetails.close')}
            >
              <span>×</span>
            </button>
          )}
        </div>
      </div>
      {/* 场次内容 */}
      <div
        ref={readonlyContentRef}
        className={`scene-content${canEdit ? '' : ' scene-content-readonly'}`}
        onMouseDownCapture={(event) => {
          if (!canEdit) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onClickCapture={(event) => {
          if (!canEdit) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onKeyDownCapture={(event) => {
          if (!canEdit) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
      >
        {renderMainContent()}
      </div>

      {/* 粘贴失败提示对话框 */}
      <AlertDialog
        visible={alertDialogVisible}
        title={t('sceneDetails.pasteFailed')}
        content={alertDialogContent}
        actions={[
          {
            label: t('sceneDetails.ok'),
            onClick: () => setAlertDialogVisible(false),
            type: "primary"
          }
        ]}
        onClose={() => setAlertDialogVisible(false)}
      />

    </div>
  );
});

SceneDetails.displayName = 'SceneDetails';

export default SceneDetails;
